import type { StripeError } from '@stripe/stripe-js';
import type { AnyFormApi } from '@tanstack/react-form';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isPlainObject } from 'es-toolkit';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { SharedConfig } from '@_/config/shared-config.ts';
import { errorCommonI18n } from '@_/i18n-common';
import { BaseForm, FormTextInput } from '../formElements/index.ts';
import {
  FormStripeCardCVCElement,
  FormStripeCardExpiryElement,
  FormStripeCardNumberElement
} from '../StripeElements/index.ts';

const stripeElementValidator = z.boolean().superRefine((value, ctx) => {
  if (!value) {
    ctx.issues.push({
      code: 'custom',
      input: value,
      params: {
        i18nMessage: errorCommonI18n.required
      }
    });
  }
});

export const creditCardFieldGroupSchema = () =>
  z.object({
    name: z.string().trim().nonempty().max(SharedConfig.user.nameMaxLength),
    number: stripeElementValidator,
    expiry: stripeElementValidator,
    cvc: stripeElementValidator
  });

export type CreditCardFieldGroupSchema = z.input<ReturnType<typeof creditCardFieldGroupSchema>>;

export const CreditCardFieldGroup = BaseForm.withFieldGroup({
  defaultValues: {
    name: '',
    number: false,
    expiry: false,
    cvc: false
  } as CreditCardFieldGroupSchema,
  render: function Render({ group }) {
    return (
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <group.AppField
            name="name"
            children={() => (
              <FormTextInput
                label={
                  <FormattedMessage
                    id="component:label.payment.credit-card-name"
                    defaultMessage="Name on card"
                  />
                }
              />
            )}
          />
        </div>

        <div className="col-span-2">
          <group.AppField
            name="number"
            children={() => (
              <FormStripeCardNumberElement
                label={
                  <FormattedMessage
                    id="component:label.payment.credit-card-no"
                    defaultMessage="Card number"
                  />
                }
              />
            )}
          />
        </div>

        <div>
          <group.AppField
            name="expiry"
            children={() => (
              <FormStripeCardExpiryElement
                label={
                  <FormattedMessage
                    id="component:label.payment.credit-card-expiry"
                    defaultMessage="Expiration"
                  />
                }
              />
            )}
          />
        </div>

        <div>
          <group.AppField
            name="cvc"
            children={() => (
              <FormStripeCardCVCElement
                label={
                  <FormattedMessage
                    id="component:label.payment.credit-card-cvc"
                    defaultMessage="CVC"
                  />
                }
              />
            )}
          />
        </div>
      </div>
    );
  }
});

export function useSubmitStripeCreditCard({ form }: { form: AnyFormApi }) {
  const elements = useElements();
  const stripe = useStripe();

  return async ({
    fieldNames,
    stripeClientSecret
  }: {
    fieldNames: Record<keyof CreditCardFieldGroupSchema, string>;
    stripeClientSecret: string;
  }): Promise<
    { success: true; stripePaymentMethodId: string } | { success: false; error: StripeError }
  > => {
    if (!stripe) {
      throw Error('stripe is required');
    }

    if (!elements) {
      throw Error('stripe elements are required');
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      throw new Error('unable to locate card element');
    }

    const name = form.getFieldValue(fieldNames.name);

    if (!name) {
      throw new Error('name is empty');
    }

    // Set up credit card and go through SCA if required
    const confirmation = await stripe.confirmCardSetup(stripeClientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name
        }
      }
    });

    if (confirmation.error) {
      const { error } = confirmation;

      let fieldName: string;
      if (error.param === 'cvc') {
        fieldName = fieldNames.cvc;
      } else if (error.param === 'number') {
        fieldName = fieldNames.number;
      } else if (error.param === 'exp_month' || error.param === 'exp_year') {
        fieldName = fieldNames.expiry;
      } else {
        fieldName = fieldNames.number;
      }

      form.setFieldMeta(fieldName, meta => ({
        ...meta,
        errorMap: {
          ...meta.errorMap,
          onChange: error.message
        }
      }));

      return {
        success: false,
        error: confirmation.error
      };
    }

    // Get id of newly added credit card from Stripe
    let stripePaymentMethodId: string;
    if (typeof confirmation.setupIntent.payment_method === 'string') {
      stripePaymentMethodId = confirmation.setupIntent.payment_method;
    } else if (isPlainObject(confirmation.setupIntent.payment_method)) {
      stripePaymentMethodId = confirmation.setupIntent.payment_method.id;
    } else {
      throw new Error('payment_method is null');
    }

    return {
      success: true,
      stripePaymentMethodId
    };
  };
}
